/**
 * Enhanced Error Handling Service
 * Provides comprehensive error handling, retry mechanisms, and user feedback
 */

import { parseApiError, isRetryableError, getRetryDelay } from '../utils/consolidatedErrorHandling.js';

export class ErrorHandlingService {
  constructor(toastService, apiClient) {
    this.toastService = toastService;
    this.apiClient = apiClient;
    this.retryAttempts = new Map(); // Track retry attempts per operation
    this.maxRetries = 3;
  }

  /**
   * Handle API errors with structured error processing
   * @param {Error} error - The error to handle
   * @param {object} context - Context information about the operation
   * @param {object} options - Error handling options
   * @returns {object} Processed error information
   */
  async handleApiError(error, context = {}, options = {}) {
    const {
      showToast = true,
      allowRetry = true,
      operation = 'unknown',
      fallbackMessage = 'An error occurred. Please try again.',
      onRetry = null,
      customActions = []
    } = options;

    let structuredError;

    // Parse the error based on its type
    if (error.response) {
      // HTTP error response
      structuredError = await parseStructuredError(error.response);
    } else if (error.name === 'AbortError') {
      // Request timeout
      structuredError = {
        code: 'TIMEOUT_ERROR',
        message: 'Request timed out',
        displayMessage: 'The request took too long to complete. Please try again.',
        canRetry: true,
        severity: 'warning',
        category: 'network'
      };
    } else if (error.message?.includes('fetch')) {
      // Network error
      structuredError = {
        code: 'NETWORK_ERROR',
        message: 'Network error',
        displayMessage: 'Unable to connect to the server. Please check your connection and try again.',
        canRetry: true,
        severity: 'error',
        category: 'network'
      };
    } else {
      // Generic error
      structuredError = {
        code: 'UNKNOWN_ERROR',
        message: error.message || 'Unknown error',
        displayMessage: fallbackMessage,
        canRetry: false,
        severity: 'error',
        category: 'general'
      };
    }

    // Add context information
    structuredError.context = context;
    structuredError.timestamp = new Date().toISOString();

    // Determine if retry is possible
    const canRetry = allowRetry && (structuredError.canRetry || isRetryableError(error));
    const currentAttempts = this.retryAttempts.get(operation) || 0;
    const hasRetriesLeft = currentAttempts < this.maxRetries;

    structuredError.canRetry = canRetry && hasRetriesLeft;
    structuredError.retryAttempt = currentAttempts;
    structuredError.maxRetries = this.maxRetries;

    // Add retry action if applicable
    if (structuredError.canRetry && onRetry) {
      structuredError.actionButtons = [
        {
          text: `Retry (${this.maxRetries - currentAttempts} left)`,
          action: () => this.handleRetry(operation, onRetry, structuredError),
          variant: 'primary'
        },
        ...customActions
      ];
    } else {
      structuredError.actionButtons = customActions;
    }

    // Show toast notification if requested
    if (showToast) {
      this.showErrorToast(structuredError);
    }

    // Log error for debugging
    this.logError(structuredError, context);

    return structuredError;
  }

  /**
   * Handle retry operations with exponential backoff
   * @param {string} operation - Operation identifier
   * @param {Function} retryFunction - Function to retry
   * @param {object} originalError - Original error information
   * @returns {Promise} Retry result
   */
  async handleRetry(operation, retryFunction, originalError) {
    const currentAttempts = this.retryAttempts.get(operation) || 0;
    const newAttempts = currentAttempts + 1;
    
    this.retryAttempts.set(operation, newAttempts);

    // Calculate delay based on error type and attempt number
    const delay = getRetryDelay(originalError, currentAttempts);
    
    // Show retry notification
    this.toastService.showInfo(
      `Retrying operation... (Attempt ${newAttempts}/${this.maxRetries})`,
      3000
    );

    // Wait for delay
    if (delay > 0) {
      await new Promise(resolve => setTimeout(resolve, delay));
    }

    try {
      const result = await retryFunction();
      
      // Clear retry attempts on success
      this.retryAttempts.delete(operation);
      
      // Show success message
      this.toastService.showSuccess('Operation completed successfully');
      
      return result;
    } catch (retryError) {
      // Handle retry failure
      return this.handleApiError(retryError, { 
        ...originalError.context, 
        isRetry: true,
        retryAttempt: newAttempts 
      }, {
        operation,
        allowRetry: newAttempts < this.maxRetries,
        onRetry: retryFunction
      });
    }
  }

  /**
   * Show error toast with appropriate styling and actions
   * @param {object} structuredError - Structured error object
   */
  showErrorToast(structuredError) {
    const { severity, displayMessage, actionButtons } = structuredError;
    
    let toastType = 'error';
    let duration = 8000; // Longer duration for errors

    switch (severity) {
      case 'warning':
        toastType = 'warning';
        duration = 6000;
        break;
      case 'info':
        toastType = 'info';
        duration = 5000;
        break;
      case 'critical':
        toastType = 'error';
        duration = 0; // Don't auto-dismiss critical errors
        break;
    }

    // Show the toast with action buttons if available
    if (actionButtons && actionButtons.length > 0) {
      this.toastService.showToast(displayMessage, toastType, duration, {
        actions: actionButtons
      });
    } else {
      this.toastService.showToast(displayMessage, toastType, duration);
    }
  }

  /**
   * Handle file operation errors with specific messaging
   * @param {Error} error - File operation error
   * @param {object} fileInfo - Information about the file
   * @param {string} operation - Type of file operation
   * @returns {object} Processed error information
   */
  async handleFileError(error, fileInfo = {}, operation = 'file_operation') {
    const { fileName = 'file', fileSize = 0, fileType = 'unknown' } = fileInfo;
    
    const context = {
      fileName,
      fileSize,
      fileType,
      operation
    };

    // Provide specific error messages for common file operation failures
    let customMessage;
    let customActions = [];

    if (error.response?.status === 413) {
      customMessage = `File "${fileName}" is too large. Maximum size is 4MB.`;
    } else if (error.response?.status === 415) {
      customMessage = `File type "${fileType}" is not supported. Only PDF, DOCX, TXT, and HTML files are allowed.`;
    } else if (error.response?.status === 404 && operation === 'delete') {
      customMessage = `File "${fileName}" not found or you don't have permission to delete it.`;
    } else if (error.response?.status === 409) {
      customMessage = `File "${fileName}" is currently being processed. Please try again in a moment.`;
      customActions = [
        {
          text: 'Check Status',
          action: () => this.checkFileStatus(fileInfo.id),
          variant: 'secondary'
        }
      ];
    }

    return this.handleApiError(error, context, {
      operation: `${operation}_${fileName}`,
      fallbackMessage: customMessage || `Failed to ${operation.replace('_', ' ')} "${fileName}". Please try again.`,
      customActions
    });
  }

  /**
   * Handle parrot assignment errors
   * @param {Error} error - Assignment error
   * @param {object} assignmentInfo - Information about the assignment
   * @returns {object} Processed error information
   */
  async handleAssignmentError(error, assignmentInfo = {}) {
    const { fileName = 'file', parrotName = 'parrot', operation = 'assign' } = assignmentInfo;
    
    const context = {
      fileName,
      parrotName,
      operation
    };

    let customMessage;
    
    if (error.response?.status === 404) {
      if (error.response.data?.error?.code === 'PARROT_NOT_FOUND') {
        customMessage = `Parrot "${parrotName}" not found.`;
      } else if (error.response.data?.error?.code === 'FILE_NOT_FOUND') {
        customMessage = `File "${fileName}" not found.`;
      }
    } else if (error.response?.status === 409) {
      customMessage = `File "${fileName}" is already assigned to "${parrotName}".`;
    }

    return this.handleApiError(error, context, {
      operation: `${operation}_${fileName}_${parrotName}`,
      fallbackMessage: customMessage || `Failed to ${operation} "${fileName}" ${operation === 'assign' ? 'to' : 'from'} "${parrotName}". Please try again.`
    });
  }

  /**
   * Check file status (placeholder for status checking functionality)
   * @param {string} fileId - File ID to check
   */
  async checkFileStatus(fileId) {
    try {
      const response = await this.apiClient.get(`/api/knowledge-base/${fileId}/status`);
      this.toastService.showInfo(`File status: ${response.status}`);
    } catch (error) {
      this.toastService.showError('Unable to check file status');
    }
  }

  /**
   * Log error for debugging and monitoring
   * @param {object} structuredError - Structured error object
   * @param {object} context - Additional context
   */
  logError(structuredError, context = {}) {
    const logData = {
      timestamp: structuredError.timestamp,
      code: structuredError.code,
      message: structuredError.message,
      severity: structuredError.severity,
      category: structuredError.category,
      context: {
        ...structuredError.context,
        ...context
      },
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorHandlingService:', logData);
    }

    // In production, you might want to send this to an error tracking service
    // Example: sendToErrorTrackingService(logData);
  }

  /**
   * Clear retry attempts for an operation
   * @param {string} operation - Operation identifier
   */
  clearRetryAttempts(operation) {
    this.retryAttempts.delete(operation);
  }

  /**
   * Clear all retry attempts
   */
  clearAllRetryAttempts() {
    this.retryAttempts.clear();
  }

  /**
   * Get user-friendly error messages for common scenarios
   * @param {string} errorCode - Error code
   * @param {object} context - Error context
   * @returns {string} User-friendly message
   */
  getUserFriendlyMessage(errorCode, context = {}) {
    const messages = {
      'FILE_NOT_FOUND': `File "${context.fileName || 'file'}" not found or you don't have permission to access it.`,
      'FILE_TOO_LARGE': `File "${context.fileName || 'file'}" is too large. Maximum size is 4MB.`,
      'INVALID_FILE_TYPE': `File type "${context.fileType || 'unknown'}" is not supported.`,
      'NETWORK_ERROR': 'Please check your internet connection and try again.',
      'TIMEOUT_ERROR': 'The request took too long to complete. Please try again.',
      'RATE_LIMIT_EXCEEDED': 'Too many requests. Please wait a moment and try again.',
      'UNAUTHORIZED': 'Please sign in to continue.',
      'FORBIDDEN': 'You don\'t have permission to perform this action.',
      'SERVER_ERROR': 'A server error occurred. Please try again later.',
      'VALIDATION_ERROR': 'Please check your input and try again.',
      'PARROT_ASSIGNMENT_FAILED': `Failed to assign "${context.fileName || 'file'}" to "${context.parrotName || 'parrot'}".`,
      'SYNC_ERROR': 'Synchronization failed. The operation will be retried automatically.',
      'QUOTA_EXCEEDED': 'You have reached your usage limit. Please upgrade your plan or try again later.'
    };

    return messages[errorCode] || 'An error occurred. Please try again.';
  }
}

export default ErrorHandlingService;
